/* istanbul ignore file */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ADPResourceService } from 'digital-core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { PGHAppLoadService } from './pgh-app-load.service';
import { IRegisteredDevice } from 'src/app/model/claims/IRegisteredDevice';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ServicesRequestsModalWindowComponent } from 'src/app/modules/dashboard/components/services-requests-modal-window/services-requests-modal-window.component';
import { CoverageEndedServiceModalComponent } from 'src/app/shared/components/coverage-ended-service-modal/coverage-ended-service-modal.component';
import { AppConstants } from '../constants';
import { PlanDetail } from 'src/app/model/plan/plan-detail';
import { CacheEntry } from 'src/app/model/cache-entry/cache-entry';
import { StorageService } from './storage.service';
import { PGHOktaService } from './pghokta.service';
import { LoggedInUserSettings } from './settings/logged-in-user-settings';

/**
 * A single plan reference sent to the GSSP auth-token endpoint as part of the
 * `Plans` array. Casing matches the BE contract (PascalCase) exactly.
 */
export interface IGSSPPlanReference {
  CertificateNumber: string;
  DealerCode: string;
  ChildCertificateNumbers: string[];
}

@Injectable({
  providedIn: 'root',
})
export class GSSPAuthenticationService {
  private matDialog = inject(MatDialog);
  private loggedInUserSettings = inject(LoggedInUserSettings);
  expApiUrl: string;
  portalUrl: string;
  clientName: string;
  constructor(
    private http: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private appLoadService: PGHAppLoadService,
    private resourceService: ADPResourceService,
    private storageService: StorageService,
    private oktaService: PGHOktaService
  ) {
    this.expApiUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl + this.preInitSettingsService.getSettings().middleWare.accountPath + 'gsspauthtoken';
    this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
      if (value) {
        this.portalUrl = this.resourceService.getPortalSetting("URL_ClaimsPortal").Value;
        this.clientName = this.resourceService.getPortalSetting("Global_SourceClientName").Value;
      }
    });
  }

  /**
   * Builds the `Plans` array for the GSSP auth-token request. Lowe's accounts can hold
   * multiple plans at once, and the BE needs every plan number + dealer code (including
   * the currently-selected plan — this is "all plans", not "the other plans") to resolve
   * claims/service eligibility across the whole account.
   *
   * `$plans` is populated for every logged-in user — `LoggedInUserSettings.setInitialPlan()`
   * runs on login and routes to tech-support when the portal profile returns no plans — so
   * the fallback below is a safety net, not a routine path. It preserves the previous
   * single-plan behaviour by echoing back the dealer/certificate the caller passed in.
   *
   * Caveat for the contract-driven call sites (header, logged-in-navlist, claims-support,
   * home-device-details): those pass a `certificateNumber` taken from `$selectedContract.id`
   * (the Elita/ePrism contract model), whereas these entries come from `IPlan.planNumber`
   * (the portal-profile model). Both describe the same Pocket Geek Home plans, but the two
   * identifier spaces are not guaranteed to match, so the top-level `CertificateNumber` may
   * not always appear inside `Plans` for those flows.
   *
   * Each entry also carries `ChildCertificateNumbers` — the child/add-on equipment certificates
   * registered under that plan. `IPlan` (from `GetPortalProfile`) never actually carries this data,
   * so it's read from that plan's cached `PlanDetail` (`PlanDetail:<planNumber>`, the same
   * per-plan cache `PlanService` populates on single/all/bulk plan-detail fetches) via its
   * `certificates[].certificateNumber` list — mirroring how callers like `dashboard.component.ts`
   * build the top-level `ChildCertificateNumbers`. Falls back to `IPlan.childCertificateNumbers`
   * (kept for forward-compat if the portal profile ever starts returning it) and then `[]` when
   * that plan's detail hasn't been cached yet. In the safety-net fallback path below, the
   * caller-supplied `childCertificateNumbers` (the same value echoed at the top level of the
   * request) is reused.
   *
   * `Plans[].DealerCode` uses each plan's own `dealerCode` as-is — same as the top-level
   * `DealerCode` and the single-plan fallback below, both of which use the caller-supplied
   * `dealerCode`.
   */
  private buildGSSPPlans(dealerCode: string, certificateNumber: string, childCertificateNumbers: string[]): IGSSPPlanReference[] {
    const plans = this.loggedInUserSettings.$plans.getValue();
    if (plans && plans.length > 0) {
      return plans.map(plan => {
        const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:${plan.planNumber}`;
        const cachedPlanDetail = this.storageService.getSession<CacheEntry<PlanDetail>>(cacheKey);
        const planChildCertificateNumbers = cachedPlanDetail?.data?.certificates?.map(c => c.certificateNumber)
          ?? plan.childCertificateNumbers
          ?? [];
        return {
          CertificateNumber: plan.planNumber,
          DealerCode: plan.dealerCode,
          ChildCertificateNumbers: planChildCertificateNumbers,
        };
      });
    }

    if (!certificateNumber) {
      return [];
    }

    return [{
      CertificateNumber: certificateNumber,
      DealerCode: dealerCode,
      ChildCertificateNumbers: childCertificateNumbers ?? [],
    }];
  }

  /**
   * `productCertificateNumber` is only used when there's no `registeredDevice`: it emits a
   * certificate-only Product so GSSP can still scope to the child certificate when the caller
   * knows the certificate but not the product (e.g. an EZ Pass claim link). See
   * certificateOnlyProduct for why the other fields are sent as blanks rather than omitted.
   */
  postGenerateGSSPToken(
    dealerCode: string,
    certificateNumber: string,
    childCertificateNumbers: string[],
    registeredDevice: IRegisteredDevice,
    claimNumber: string = null,
    productCertificateNumber: string | null = null
  ): Observable<any> {
    const cacheKey = `${AppConstants.STORAGE_KEYS.PlanDetail}:${certificateNumber}`;
    const cacheEntry = this.storageService.getSession<CacheEntry<PlanDetail>>(cacheKey);
    // The session cache is only warm for plans that went through a plan-detail fetch (and gets
    // cleared after add-equipment / earlier handoffs). A miss must not be read as "multi-cert",
    // so fall back to the in-memory plan details of the owning policy.
    const owningPlanDetail: PlanDetail | null = cacheEntry?.data
      ?? this.loggedInUserSettings.$planDetails.getValue()?.find(pd => pd.planNumber === certificateNumber)
      ?? (this.loggedInUserSettings.$selectedPlanDetail.getValue()?.planNumber === certificateNumber
        ? this.loggedInUserSettings.$selectedPlanDetail.getValue()
        : null);
    const oktaUserId = this.oktaService.getIdToken()?.claims?.sub ?? null;
    const body = {
      ClientName: this.clientName,
      TokenRequestInformation: {
        userIdentifier: oktaUserId,
        DealerCode: dealerCode,
        CertificateNumber: certificateNumber,
        ClaimNumber: claimNumber,
        ChildCertificateNumbers: childCertificateNumbers,
        Plans: this.buildGSSPPlans(dealerCode, certificateNumber, childCertificateNumbers),
        // cacheEntry can be missing for a plan that was resolved without ever going through the
        // single/all/bulk plan-detail fetch that warms this cache (e.g. a multi-policy claim
        // whose owning plan differs from $selectedPlanDetail) — don't crash the handover over a label.
        PlanType: cacheEntry?.data?.planType ? this.resourceService.getViewLabel(`MyPlan_${cacheEntry.data.planType}_Protection_Label`) : null,
        Product: registeredDevice == null ? this.certificateOnlyProduct(productCertificateNumber) : {
          certificateNumber: registeredDevice.certificateNumber ?? childCertificateNumbers[0],
          manufacturer: registeredDevice.manufacturer,
          make: registeredDevice.manufacturer,
          model: registeredDevice.model,
          imei: registeredDevice.imeiNumber ?? '',
          serialNumber: registeredDevice.serialNumber,
          skuNumber: registeredDevice.dealerSkuNumber ?? '',
          imageUrl: '',
          memory: registeredDevice.memoryCode ?? '',
          color: registeredDevice.colorCode ?? '',
          size: '',
          deviceType: registeredDevice.itemDescription,
          description: registeredDevice.itemDescription,
          riskTypeCode: '',
          riskTypeDescription: '',
          isMobile: 'false',
          name: registeredDevice.registeredItemName,
          registeredName: registeredDevice.registeredItemName,
          registeredDevices: '',
          type: registeredDevice.deviceTypeCode,
          purchaseDate: registeredDevice.purchasedDate,
          purchasePrice: registeredDevice.purchasePrice,          
        },
        Customer : registeredDevice == null ? null : {
          firstName: registeredDevice.firstName ?? '',
          lastName: registeredDevice.lastName ?? '',
          homePhone: registeredDevice.homePhone ?? {},
          cellPhone: registeredDevice.cellPhone ?? {},
          // Null (not '') when the policy has no email on file — e.g. an email-less Lowe's customer.
          email: registeredDevice.email ?? null,
          address: registeredDevice.address ?? '',
          city: registeredDevice.city ?? '',
          country: registeredDevice.country ?? '',
          postalCode: registeredDevice.postalCode ?? '',
          state: registeredDevice.state ?? '',
        },
        // LPP (single-cert) plans don't support GSSP's product-picker question — only MAPP
        // (multi-cert) plans do — so preSelectedQuestion must be null whenever the owning
        // plan's isMultiCertPolicy is explicitly false, even if a registeredDevice was passed.
        preSelectedQuestion: registeredDevice == null || owningPlanDetail?.isMultiCertPolicy === false ? null : {
          coverageType: registeredDevice.coverageTypeCode,
          flowType: registeredDevice.serviceType
        },
      }
    };
    // Log and re-throw: callers own the user-facing failure handling (snackbar / state reset),
    // so the error must still reach their subscribe({ error }) arm.
    return this.http.post<IRegisteredDevice>(this.expApiUrl, body).pipe(
      catchError((error) => {
        console.error('GSSP auth token request failed', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * The middleware deserializes Product into its TokenProductInformation model and re-serializes
   * it for GSSP, so any field left out here reaches GSSP as an explicit `null` - which GSSP
   * rejects (400 "Problem retrieving authorization"). Blank strings mirror what the
   * registered-device Product sends for unknown values; purchaseDate/purchasePrice stay unset,
   * same as a device with no purchase info.
   */
  private certificateOnlyProduct(certificateNumber: string | null) {
    if (!certificateNumber) {
      return null;
    }
    return {
      certificateNumber,
      manufacturer: '',
      make: '',
      model: '',
      imei: '',
      serialNumber: '',
      skuNumber: '',
      imageUrl: '',
      memory: '',
      color: '',
      size: '',
      deviceType: '',
      description: '',
      riskTypeCode: '',
      riskTypeDescription: '',
      isMobile: 'false',
      name: '',
      registeredName: '',
      registeredDevices: '',
      type: '',
    };
  }

  openServiceRequestModal(title = 'Dashboard_ServiceRequest_Modal_Title', description = 'Dashboard_ServiceRequest_Modal_Description'): void {
    this.matDialog.open(ServicesRequestsModalWindowComponent, {
      data: {
        model: {
          phoneNumber: this.resourceService.getViewLabel('Global_PhoneNumber'),
        },
        title: title,
        desc: description
      },
      autoFocus: false,
      height: 'auto',
      disableClose: false
    });
  }

  /**
   * Opens the "coverage has ended" warning modal shown to expired users before the GSSP
   * service/claims handoff. The dialog closes with `true` when the user opts to continue.
   */
  openCoverageEndedServiceModal(): MatDialogRef<CoverageEndedServiceModalComponent, boolean> {
    return this.matDialog.open<CoverageEndedServiceModalComponent, undefined, boolean>(CoverageEndedServiceModalComponent, {
      autoFocus: false,
      ariaLabelledBy: 'coverageEndedServiceModalTitle',
    });
  }

  generateFileClaimLink(token: string) {
    return this.portalUrl + '?accessToken=' + token + '&redirectUrl=/file-claim';
  }

  get path(): string | null {
    const isProduction = this.preInitSettingsService.getSettings()?.isProduction?.toLowerCase() === 'true';
    if(isProduction) {
      return null;
    }

    const elitaEnv = this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ElitaEnvironment) ?? this.storageService.getLocal<string>(AppConstants.STORAGE_KEYS.ElitaEnvironmentDefault);

    // if elitaenv start with U1/U2/U3 strip the U and return the env number, otherwise return null
    if (elitaEnv && elitaEnv.toUpperCase().startsWith('U')) {
      return elitaEnv.substring(1);
    }

    return null;
  }

  appendPathParam(url: string): string {
    const path = this.path;
    return path ? `${url}&path=${path}` : url;
  }
}
